import React, { useState } from 'react';
import { Button, Dimmer, Form, Loader, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { isContractor } from '@utils/UserRoleSelector';
import usePostFiles from '@api/filesUpload/hooks/usePostFiles';
import useGetUser from '@api/user/hooks/useGetUser';
import UploadDragAndDrop from './UploadDragAndDrop';

type Props = {
  triggerUploadsTableRefresh: (uploadsTableRefresh: boolean) => void;
};

function UploadModal({ triggerUploadsTableRefresh }: Props) {
  const [open, setOpen] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  const [filesToSubmit, setFilesToSubmit] = useState<File[] | null | undefined>();
  const { data: user } = useGetUser();

  const { t } = useTranslation();
  const postFiles = usePostFiles(
    setOpen,
    setModalErrorMessage,
    setIsSubmitDisabled,
    triggerUploadsTableRefresh
  );

  const onSubmit = () => {
    if (filesToSubmit) {
      setIsSubmitDisabled(true);
      postFiles(filesToSubmit);
    }
  };

  const onClose = () => {
    setOpen(false);
    setModalErrorMessage(null);
  };

  if (!user || !isContractor(user)) {
    return null;
  }

  return (
    <Modal
      as={Form}
      onSubmit={onSubmit}
      closeIcon
      onClose={onClose}
      dimmer
      onOpen={() => {
        setOpen(true);
        triggerUploadsTableRefresh(false);
      }}
      open={open}
      trigger={<Button content={t('upload.file')} icon="upload" labelPosition="left" positive />}
    >
      <Modal.Header>{t('upload.file.title')}</Modal.Header>
      <Modal.Content>
        {isSubmitDisabled && (
          <Dimmer active inverted>
            <Loader>{t('upload.file.wait')}</Loader>
          </Dimmer>
        )}
        <Form.Field>
          <UploadDragAndDrop
            setFilesToSubmit={setFilesToSubmit}
            modalErrorMessage={modalErrorMessage}
          />
        </Form.Field>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('button.cancel')}</Button>
        <Button
          type="submit"
          content={t('button.save')}
          labelPosition="right"
          icon="checkmark"
          positive
          disabled={isSubmitDisabled}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default UploadModal;
